/* Copyright (C) Okahu Inc 2023-2024. All rights reserved. */
'use client';

import {
  FC,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { useSearchParams } from 'next/navigation';
import { DateRangeContext } from '@/context';
import { useLanguage } from '@/providers/LanguageProvider';
import { getActiveLocale } from '@/ux-common/src/utils/locale';

import { useKahuDockState } from '@/hooks/useKahuDockState';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { CustomButton } from '@/components/common';
import { System } from '@/components/icons';

import {
  checkPreset,
  DateInput,
  DateRange,
  DateRangeActions,
  DateRangeDisplay,
  DateRangePickerProps,
  getDateAdjustedForTimezone,
  PresetButton,
  setPreset,
  TimeInput,
} from './calendar-helper';
import { useMostRecentPreset, usePresets } from './calendar-helper/presets';
import { Preset } from './calendar-helper/types';

/** The DateRangePicker component allows a user to select a range of dates */
const DateRangePicker: FC<DateRangePickerProps> = ({
  initialDateFrom = new Date(new Date().setHours(0, 0, 0, 0)),
  initialDateTo,
  initialCompareFrom,
  initialCompareTo,
  onUpdate,
  todaysDate,
  align = 'start',
  locale,
  currentPage,
  onRangeChange,
  classNames,
  customMessageForPreset,
  initialRange,
  factName,
}) => {
  const activeLocale = getActiveLocale(locale);
  const dateRangeContext = useContext(DateRangeContext);
  const [isOpen, setIsOpen] = useState(false);
  const presets = usePresets();
  const mostRecentPresets = useMostRecentPreset(factName);
  const searchParams = useSearchParams();
  const daysParam = searchParams.get('days');
  const startParam = searchParams.get('start');
  const endParam = searchParams.get('end');
  const { messages } = useLanguage();
  const langData = messages?.PromptFilters;

  const [selectedMostRecent, setSelectedMostRecent] = useState<number | null>(
    null
  );

  const openedMostRecentRef = useRef<number | null>(null);
  const openedPresetRef = useRef<string | undefined>(undefined);

  // Get initial range based on URL parameters
  const getInitialRange = () => {
    const today = todaysDate ? todaysDate : new Date();
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(today.getDate() - 29);

    const sevenDaysAgo = new Date(today);
    sevenDaysAgo.setDate(today.getDate() - 6);

    // If start and end params exist, use them
    if (startParam && endParam) {
      return {
        from: new Date(startParam),
        to: new Date(endParam),
      };
    } else if (daysParam === 'all') {
      return {
        from: undefined,
        to: undefined,
      };
    }

    if (currentPage === 'architecture') {
      return {
        from: undefined,
        to: undefined,
      };
    }

    // Default to last 7 days if no params
    today.setHours(23, 59, 59, 999);
    sevenDaysAgo.setHours(0, 0, 0, 0);
    return {
      from: getDateAdjustedForTimezone(sevenDaysAgo),
      to: getDateAdjustedForTimezone(today),
    };
  };

  const [timeRange, setTimeRange] = useState<{
    from: string;
    to: string;
    mostRecent?: number | null;
  }>({
    from: '00:00', // Set default instead of empty string
    to: '23:59', // Set default instead of empty string
    mostRecent: null,
  });

  const [range, setRange] = useState<DateRange>(getInitialRange());

  const [rangeCompare, setRangeCompare] = useState<DateRange | undefined>(
    initialCompareFrom
      ? {
          from: new Date(new Date(initialCompareFrom).setHours(0, 0, 0, 0)),
          to: initialCompareTo
            ? new Date(new Date(initialCompareTo).setHours(0, 0, 0, 0))
            : new Date(new Date(initialCompareFrom).setHours(0, 0, 0, 0)),
        }
      : undefined
  );
  // Refs to store the values of range and rangeCompare when the date picker is opened
  const openedRangeRef = useRef<DateRange | undefined>();
  const openedRangeCompareRef = useRef<DateRange | undefined>();

  // Ref and state to measure max width of date range container
  const dateRangeContainerWidthRef = useRef<HTMLDivElement>(null);
  const [maxWidth, setMaxWidth] = useState<number>(800);

  // Active tab state within date range picker
  const [activeTab, setActiveTab] = useState<string>(
    selectedMostRecent != null ? 'tab-2' : 'tab-1'
  );

  const [selectedPreset, setSelectedPreset] = useState<string | undefined>();
  const [isSmallScreen, setIsSmallScreen] = useState(
    typeof window !== 'undefined' ? window.innerWidth < 960 : false
  );

  const { isDocked } = useKahuDockState();

  const handleMostRecentPreset = (preset: Preset) => {
    setSelectedPreset(preset.name);
    setSelectedMostRecent(preset.count || null);

    setRange({
      from: undefined,
      to: undefined,
      mostRecent: preset.count ?? null,
    });

    // Keep valid time values, don't set to empty strings
    setTimeRange({
      from: '00:00',
      to: '23:59',
      mostRecent: preset.count ?? null,
    });
  };

  useEffect(() => {
    if (!isOpen) return;

    if (selectedMostRecent != null) {
      setActiveTab('tab-2'); // Most Recent
    } else {
      setActiveTab('tab-1'); // Time Range
    }
  }, [isOpen, selectedMostRecent]);

  // Store opened values when popover is opened
  useEffect(() => {
    if (isOpen) {
      openedRangeRef.current = range;
      openedRangeCompareRef.current = rangeCompare;
      openedMostRecentRef.current = selectedMostRecent;
      openedPresetRef.current = selectedPreset;
    }
  }, [isOpen]);

  useEffect(() => {
    const handleResize = (): void => {
      setIsSmallScreen(window.innerWidth < 960);
    };

    window.addEventListener('resize', handleResize);

    // Clean up event listener on unmount
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Update range when URL parameter changes
  useEffect(() => {
    const startParam = searchParams.get('start');
    const endParam = searchParams.get('end');
    // const presetName = searchParams.get('presetName');
    if (startParam && endParam) {
      const newRange = {
        from: new Date(startParam),
        to: new Date(endParam),
      };
      setRange(newRange);
    }
  }, [searchParams]);

  const resetValues = (): void => {
    // Reset selected preset and most-recent count
    setSelectedPreset(openedPresetRef.current);
    setSelectedMostRecent(openedMostRecentRef.current);

    if (openedRangeRef.current) {
      setRange(openedRangeRef.current);
    } else {
      setRange({
        from:
          typeof initialDateFrom === 'string'
            ? getDateAdjustedForTimezone(initialDateFrom)
            : initialDateFrom,
        to: initialDateTo
          ? typeof initialDateTo === 'string'
            ? getDateAdjustedForTimezone(initialDateTo)
            : initialDateTo
          : typeof initialDateFrom === 'string'
            ? getDateAdjustedForTimezone(initialDateFrom)
            : initialDateFrom,
      });
    }

    if (openedRangeCompareRef.current) {
      setRangeCompare(openedRangeCompareRef.current);
    } else {
      setRangeCompare(
        initialCompareFrom
          ? {
              from:
                typeof initialCompareFrom === 'string'
                  ? getDateAdjustedForTimezone(initialCompareFrom)
                  : initialCompareFrom,
              to: initialCompareTo
                ? typeof initialCompareTo === 'string'
                  ? getDateAdjustedForTimezone(initialCompareTo)
                  : initialCompareTo
                : typeof initialCompareFrom === 'string'
                  ? getDateAdjustedForTimezone(initialCompareFrom)
                  : initialCompareFrom,
            }
          : undefined
      );
    }

    if (openedMostRecentRef.current != null) {
      setActiveTab('tab-2');
    } else {
      setActiveTab('tab-1');
    }
  };

  // Replace the useEffect that calls checkPreset with this fixed version:
  useEffect(() => {
    // If the currently selected preset is a most-recent preset, skip auto-detect
    const isMostRecentPresetSelected = mostRecentPresets.some(
      (p) => p.name === selectedPreset
    );

    // Or skip if most-recent count is set (range was set to most-recent mode)
    const isMostRecentRange =
      range.from === undefined &&
      range.to === undefined &&
      selectedMostRecent != null;

    if (isMostRecentPresetSelected || isMostRecentRange) {
      // Still inform parent about the change if needed
      if (onRangeChange) {
        onRangeChange(range, selectedPreset);
      }
      return; // skip checkPreset so it won't overwrite selectedPreset
    }

    // Only run checkPreset for date-based ranges
    checkPreset(
      presets,
      range,
      setSelectedPreset,
      currentPage === 'architecture'
    );

    if (onRangeChange) {
      onRangeChange(range, selectedPreset);
    }
  }, [range, selectedPreset, selectedMostRecent, currentPage]);

  useEffect(() => {
    if (isOpen) {
      openedRangeRef.current = range;
      openedRangeCompareRef.current = rangeCompare;
    }
  }, [isOpen]);

  const isAllTimeSelected = selectedPreset === 'allTime';
  const isTillLastDiscoveredSelected = selectedPreset === 'tillLastDiscovered';

  const handlePresetChange = (presetName: string) => {
    setSelectedPreset(presetName);

    const mostRecentPreset = mostRecentPresets.find(
      (pr) => pr.name === presetName
    );

    if (mostRecentPreset) {
      setSelectedMostRecent(mostRecentPreset.count || null);
      setRange({
        from: undefined,
        to: undefined,
        mostRecent: mostRecentPreset.count,
      });
      setTimeRange({
        from: '00:00',
        to: '23:59',
        mostRecent: mostRecentPreset.count ?? null,
      });
    } else if (
      presetName === 'allTime' ||
      presetName === 'tillLastDiscovered'
    ) {
      setSelectedMostRecent(null);
      setRange({
        from: undefined,
        to: undefined,
        mostRecent: null,
      });
      setTimeRange({
        from: '00:00',
        to: '23:59',
        mostRecent: null,
      });
    } else {
      setSelectedMostRecent(null);
      setPreset(presetName, presets, setRange, rangeCompare, setRangeCompare);
    }
  };

  const handleCalendarSelect = (
    value: { from?: Date; to?: Date } | undefined
  ) => {
    if (value?.from) {
      setSelectedPreset(undefined);
      setSelectedMostRecent(null); // Clear most-recent count when manually selecting dates

      // Set default times if timeRange is empty
      const defaultFromTime = timeRange.from || '00:00';
      const defaultToTime = timeRange.to || '23:59';

      // extract hours and minutes to preserve time when selecting calendar dates
      const [fromHour, fromMinute] = defaultFromTime.split(':').map(Number);
      const [toHour, toMinute] = defaultToTime.split(':').map(Number);

      // apply selected start time to the calendar date
      const newFrom = new Date(value.from);
      newFrom.setHours(fromHour, fromMinute, 0, 0);

      if (!value.to) {
        setRange({ from: newFrom, to: newFrom, mostRecent: null });
      } else {
        // apply selected end time to the calendar date
        const newTo = new Date(value.to);
        newTo.setHours(toHour, toMinute, 0, 0);
        setRange({ from: newFrom, to: newTo, mostRecent: null });
      }

      // Ensure timeRange has valid values
      if (!timeRange.from || !timeRange.to) {
        setTimeRange({
          from: defaultFromTime,
          to: defaultToTime,
          mostRecent: null,
        });
      }
    }
  };

  function getCurrentTimeHHMM() {
    const now = new Date();
    const hour = now.getHours().toString().padStart(2, '0');
    const min = now.getMinutes().toString().padStart(2, '0');
    return `${hour}:${min}`;
  }

  function isDateToday(date?: Date) {
    if (!date) return false;
    const today = new Date();
    return (
      date.getFullYear() === today.getFullYear() &&
      date.getMonth() === today.getMonth() &&
      date.getDate() === today.getDate()
    );
  }

  const currentTime = getCurrentTimeHHMM();

  // Helper to generate time options (every 30 minutes)
  const generateTimeOptions = () => {
    const times: string[] = [];
    for (let h = 0; h < 24; h++) {
      for (let m = 0; m < 60; m += 30) {
        // <-- 30 min interval
        const hour = h.toString().padStart(2, '0');
        const min = m.toString().padStart(2, '0');
        times.push(`${hour}:${min}`);
      }
    }
    return times;
  };

  const timeOptions = generateTimeOptions();

  const filteredFromTimeOptions = isDateToday(range.from)
    ? timeOptions.filter((t) => t <= currentTime)
    : timeOptions;

  const filteredToTimeOptions =
    // isDateToday(range.to)
    // ? timeOptions.filter((t) => t <= currentTime && t >= timeRange.from):
    timeOptions.filter((t) => t >= timeRange.from);

  const handleFromTimeChange = (value: string) => {
    setTimeRange((tr) => ({ ...tr, from: value }));
  };

  const handleToTimeChange = (value: string) => {
    setTimeRange((tr) => ({ ...tr, to: value }));
  };

  // This is used to set the range to the initial date range that comes form the default_date_range property of the app
  useEffect(() => {
    if (initialRange) {
      setRange(initialRange);
    }
  }, [initialRange]);

  useEffect(() => {
    // On mount, extract time from range and set timeRange
    if (range.from && range.to) {
      const fromTime = `${range.from.getHours().toString().padStart(2, '0')}:${range.from.getMinutes().toString().padStart(2, '0')}`;
      const toTime = `${range.to.getHours().toString().padStart(2, '0')}:${range.to.getMinutes().toString().padStart(2, '0')}`;
      setTimeRange({ from: fromTime, to: toTime });
    }
    // Only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const lastRangeRef = useRef<DateRange>(range);
  const lastTimeRangeRef = useRef<{ from: string; to: string }>(timeRange);

  // Sync timeRange when range changes (only if time part changed)
  useEffect(() => {
    // only sync time from presets, not custom dates
    if (!selectedPreset) return;

    const fromTime = range.from
      ? `${range.from.getHours().toString().padStart(2, '0')}:${range.from.getMinutes().toString().padStart(2, '0')}`
      : '00:00';
    const toTime = range.to
      ? `${range.to.getHours().toString().padStart(2, '0')}:${range.to.getMinutes().toString().padStart(2, '0')}`
      : '23:59';
    if (
      fromTime !== lastTimeRangeRef.current.from ||
      toTime !== lastTimeRangeRef.current.to
    ) {
      setTimeRange({ from: fromTime, to: toTime });
      lastTimeRangeRef.current = { from: fromTime, to: toTime };
    }
    lastRangeRef.current = range;
  }, [range]);

  // Sync range when timeRange changes (only if time part changed)
  useEffect(() => {
    if (timeRange.from === '' && timeRange.to === '') {
      return;
    }
    const prev = lastRangeRef.current;
    const fromTime = prev.from
      ? `${prev.from.getHours().toString().padStart(2, '0')}:${prev.from.getMinutes().toString().padStart(2, '0')}`
      : '';
    const toTime = prev.to
      ? `${prev.to.getHours().toString().padStart(2, '0')}:${prev.to.getMinutes().toString().padStart(2, '0')}`
      : '';
    if (timeRange.from !== fromTime || timeRange.to !== toTime) {
      setRange((prev) => {
        const newFrom = prev.from
          ? new Date(
              prev.from.getFullYear(),
              prev.from.getMonth(),
              prev.from.getDate(),
              parseInt(timeRange.from.split(':')[0], 10),
              parseInt(timeRange.from.split(':')[1], 10),
              0,
              0
            )
          : prev.from;
        const newTo = prev.to
          ? new Date(
              prev.to.getFullYear(),
              prev.to.getMonth(),
              prev.to.getDate(),
              parseInt(timeRange.to.split(':')[0], 10),
              parseInt(timeRange.to.split(':')[1], 10),
              0,
              0
            )
          : prev.to;
        return { ...prev, from: newFrom, to: newTo };
      });
    }
    lastTimeRangeRef.current = timeRange;
  }, [timeRange]);
  // to measure and set max width for both tabs within date-range container
  useLayoutEffect(() => {
    if (isOpen && dateRangeContainerWidthRef.current) {
      const maxWidth = dateRangeContainerWidthRef.current.offsetWidth;
      setMaxWidth(maxWidth);
    }
  }, [isOpen, activeTab]);

  // render the date range picker as per the pages (insights, architecture, traces, prompts)
  const alignmentOfDateRangePicker = isDocked
    ? currentPage === 'insights' || currentPage === 'architecture'
      ? 'end'
      : currentPage === 'traces' || currentPage === 'prompts'
        ? 'center'
        : align
    : align;

  return (
    <Popover
      open={isOpen}
      onOpenChange={(open: boolean) => {
        if (!open) {
          resetValues();
        }
        setIsOpen(open);
      }}
    >
      <PopoverTrigger asChild>
        <CustomButton
          customVariant="secondary"
          className={`w-full justify-start border-form-control-border-rest px-0 py-8 hover:bg-transparent active:bg-transparent ${classNames?.rangeTriggerClassName}`}
        >
          <div
            className={`flex w-full items-start justify-between ${classNames?.triggerIconClassName}`}
          >
            <DateRangeDisplay
              range={range}
              rangeCompare={rangeCompare}
              locale={activeLocale}
              selectedPreset={selectedPreset}
              classNames={classNames}
              timeRange={timeRange}
              factName={factName}
            />
            <div
              className={`scale-125 text-form-input-rest ${classNames?.triggerIconClassName}`}
            >
              {isOpen
                ? System.ChevronUpIcon('size-4')
                : System.ChevronDownIcon('size-4')}
            </div>
          </div>
        </CustomButton>
      </PopoverTrigger>
      <PopoverContent
        sideOffset={2}
        align={alignmentOfDateRangePicker}
        className="w-auto rounded-lg border border-line-secondary bg-card-01 px-[30px] py-6 shadow-lg"
      >
        {currentPage === 'traces' ? (
          <Tabs
            value={activeTab}
            onValueChange={(value) => setActiveTab(value)}
          >
            <TabsList className="m-0 mb-4 ml-1 h-[60px] w-full justify-start space-x-8 rounded-none border-b border-[#40565A] bg-transparent p-0 ">
              <TabsTrigger
                value="tab-1"
                className="px-0 text-base font-medium text-link-active decoration-[3px] underline-offset-[21px] hover:underline data-[state=active]:bg-transparent data-[state=active]:underline"
              >
                By time range
              </TabsTrigger>
              <TabsTrigger
                value="tab-2"
                className="px-0 text-base font-medium text-link-active decoration-[3px] underline-offset-[21px] hover:underline data-[state=active]:bg-transparent data-[state=active]:underline"
              >
                Most recent
              </TabsTrigger>
            </TabsList>
            <TabsContent value="tab-1" className="mt-0">
              <div
                ref={dateRangeContainerWidthRef}
                className="flex min-h-[366px] flex-row"
                style={maxWidth ? { width: `${maxWidth}px` } : undefined}
              >
                {/* Left column: Date Range & Time Range */}
                <div className="flex flex-col">
                  {/* Date Range Inputs */}
                  {!isAllTimeSelected && !isTillLastDiscoveredSelected ? (
                    <div className="mb-4 ml-1 flex items-center gap-2">
                      {/* From Date */}
                      <DateInput
                        value={range.from}
                        locale={activeLocale}
                        onChange={(date) => {
                          setSelectedPreset(undefined);
                          setRange((prevRange) => ({
                            ...prevRange,
                            from: date,
                            mostRecent: null,
                          }));
                        }}
                      />
                      {/* From Time */}
                      <TimeInput
                        value={timeRange.from}
                        onChange={handleFromTimeChange}
                        options={filteredFromTimeOptions}
                        placeholder="From Time"
                      />
                      {/* Separator */}
                      <div className="py-1">-</div>
                      {/* To Date */}
                      <DateInput
                        value={range.to}
                        locale={activeLocale}
                        onChange={(date) => {
                          setSelectedPreset(undefined);
                          setRange((prevRange) => ({
                            ...prevRange,
                            to: date,
                            mostRecent: null,
                          }));
                        }}
                      />
                      {/* To Time */}
                      <TimeInput
                        value={timeRange.to}
                        onChange={handleToTimeChange}
                        options={filteredToTimeOptions}
                        placeholder="To Time"
                      />
                    </div>
                  ) : (
                    <div className="ml-3 flex items-center gap-1">
                      {System.InfoIcon(
                        'h-[18px] w-[18px] text-form-input-disabled'
                      )}{' '}
                      <span className="text-[14px] font-normal leading-[19.6px] text-body-subtle">
                        {langData?.date_range.all_time_info_msg}
                      </span>
                    </div>
                  )}
                  {/* show validation error if start date is after end date, or start time is later than or equal to end time on same day */}
                  {range.from &&
                    range.to &&
                    (range.from > range.to ||
                      (range.from.toDateString() === range.to.toDateString() &&
                        timeRange.from >= timeRange.to)) && (
                      <div className="mb-4 ml-1 flex items-center gap-2 rounded border border-notification-error-dark bg-[#F26C742E] px-3 py-2 text-body text-notification-error-light">
                        {System.CloseCircleIcon('size-4 shrink-0')}
                        <span>{langData?.date_range.invalid_time_range}</span>
                      </div>
                    )}

                  {/* Calendar */}
                  <div className="flex">
                    <Calendar
                      mode="range"
                      onSelect={handleCalendarSelect}
                      selected={range}
                      numberOfMonths={isSmallScreen ? 1 : 2}
                      defaultMonth={
                        new Date(
                          new Date().setMonth(
                            new Date().getMonth() - (isSmallScreen ? 0 : 1)
                          )
                        )
                      }
                    />
                  </div>
                </div>

                <Separator
                  orientation="vertical"
                  className="h-92 mx-4 bg-white/[0.08]"
                />

                {/* Right column: Presets */}
                <div className="flex flex-col items-start justify-between gap-1">
                  {presets.map((preset) => (
                    <PresetButton
                      key={preset.name}
                      label={preset.label}
                      isSelected={selectedPreset === preset.name}
                      onClick={() => handlePresetChange(preset.name)}
                    />
                  ))}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="tab-2" className="mt-0">
              <div
                className="flex min-h-[366px] flex-row"
                style={maxWidth ? { width: `${maxWidth}px` } : undefined}
              >
                <div className="flex flex-col">
                  {mostRecentPresets.map((preset) => (
                    <PresetButton
                      key={preset.name}
                      label={preset.label}
                      isSelected={selectedMostRecent === preset.count}
                      onClick={() => {
                        handleMostRecentPreset(preset);
                      }}
                    />
                  ))}
                </div>
              </div>
            </TabsContent>
          </Tabs>
        ) : (
          <div className="flex min-h-[366px] flex-row">
            {/* Left column: Date Range & Time Range */}
            <div className="flex flex-col">
              {/* Date Range Inputs */}
              {!isAllTimeSelected && !isTillLastDiscoveredSelected ? (
                <div className="mb-4 ml-1 flex items-center gap-2">
                  {/* From Date */}
                  <DateInput
                    value={range.from}
                    locale={activeLocale}
                    onChange={(date) => {
                      setSelectedPreset(undefined);
                      setRange((prevRange) => ({
                        ...prevRange,
                        from: date,
                      }));
                    }}
                  />
                  {/* From Time */}
                  <TimeInput
                    value={timeRange.from}
                    onChange={handleFromTimeChange}
                    options={filteredFromTimeOptions}
                    placeholder="From Time"
                  />
                  {/* Separator */}
                  <div className="py-1">-</div>
                  {/* To Date */}
                  <DateInput
                    value={range.to}
                    locale={activeLocale}
                    onChange={(date) => {
                      setSelectedPreset(undefined);
                      setRange((prevRange) => ({
                        ...prevRange,
                        to: date,
                      }));
                    }}
                  />
                  {/* To Time */}
                  <TimeInput
                    value={timeRange.to}
                    onChange={handleToTimeChange}
                    options={filteredToTimeOptions}
                    placeholder="To Time"
                  />
                </div>
              ) : (
                <div className="ml-3 flex items-center gap-1">
                  {System.InfoIcon(
                    'h-[18px] w-[18px] text-form-input-disabled'
                  )}{' '}
                  <span className="text-[14px] font-normal leading-[19.6px] text-body-subtle">
                    {currentPage === 'architecture'
                      ? customMessageForPreset
                      : langData?.date_range.all_time_info_msg}
                  </span>
                </div>
              )}
              {/* show validation error if start date is after end date, or start time is later than or equal to end time on same day */}
              {range.from &&
                range.to &&
                (range.from > range.to ||
                  (range.from.toDateString() === range.to.toDateString() &&
                    timeRange.from >= timeRange.to)) && (
                  <div className="mb-4 ml-1 flex items-center gap-2 rounded border border-notification-error-dark bg-[#F26C742E] px-3 py-2 text-body text-notification-error-light">
                    {System.CloseCircleIcon('size-4 shrink-0')}
                    <span>{langData?.date_range.invalid_time_range}</span>
                  </div>
                )}
              {/* Calendar */}
              <div className="flex">
                <Calendar
                  mode="range"
                  onSelect={handleCalendarSelect}
                  selected={range}
                  numberOfMonths={isSmallScreen ? 1 : 2}
                  defaultMonth={
                    new Date(
                      new Date().setMonth(
                        new Date().getMonth() - (isSmallScreen ? 0 : 1)
                      )
                    )
                  }
                />
              </div>
            </div>

            <Separator
              orientation="vertical"
              className="h-92 mx-4 bg-white/[0.08]"
            />

            {/* Right column: Presets */}
            <div className="flex flex-col items-start justify-between gap-1">
              {presets.map((preset) => (
                <PresetButton
                  key={preset.name}
                  label={preset.label}
                  isSelected={selectedPreset === preset.name}
                  onClick={() => handlePresetChange(preset.name)}
                />
              ))}
            </div>
          </div>
        )}
        <DateRangeActions
          setIsOpen={setIsOpen}
          range={range}
          rangeCompare={rangeCompare}
          openedRangeRef={openedRangeRef}
          openedMostRecentRef={openedMostRecentRef}
          openedPresetRef={openedPresetRef}
          selectedPreset={selectedPreset}
          openedRangeCompareRef={openedRangeCompareRef}
          selectedMostRecent={selectedMostRecent}
          isInvalid={
            range.from &&
            range.to &&
            (range.from > range.to ||
              (range.from.toDateString() === range.to.toDateString() &&
                timeRange.from >= timeRange.to))
          }
          onUpdate={(updatedRange) => {
            if (onUpdate) {
              onUpdate({
                range: updatedRange.range,
                rangeCompare: updatedRange.rangeCompare,
                presetName: selectedPreset,
                mostRecent: updatedRange.mostRecent,
              });
            }
            /* set the date range of the context. */
            if (dateRangeContext?.setRange) {
              dateRangeContext.setRange({
                from: updatedRange.range?.from ?? null,
                to: updatedRange.range?.to ?? null,
                selectedPreset: selectedPreset ?? null,
              });
            }
          }}
          resetValues={resetValues}
        />
      </PopoverContent>
    </Popover>
  );
};

export { DateRangePicker };
